import { Alert, Stack, TextField, Typography } from '@mui/material';

import { getUnitTypeFullLabel } from '@/lib/seed-content';
import type { UnitType } from '@/types/database';

interface ConfirmIdentityStepProps {
  unitName: string;
  unitType: UnitType | null;
  unitIdentifier: string;
  onChangeUnitIdentifier: (value: string) => void;
  councilName: string;
  onChangeCouncilName: (value: string) => void;
  districtName: string;
  onChangeDistrictName: (value: string) => void;
  charteredOrgName: string;
  onChangeCharteredOrgName: (value: string) => void;
  // Only unit admins can write the shared unit_details record (its RLS keys
  // off the same admin role as every unit_* table). Non-admins still see the
  // values pulled from that record, just read-only.
  editable: boolean;
}

// Context inheritance (spec section 4, Step 0-1): the user arrives already
// authenticated with unit type/name already known from Clerk — this
// screen confirms rather than re-asks. Number/council/district/chartered-org
// are seeded from the shared unit_details record, so anything already set in
// the hub's Details page shows up pre-filled here; saving writes back to that
// same record rather than a separate site-builder copy.
export function ConfirmIdentityStep({
  unitName,
  unitType,
  unitIdentifier,
  onChangeUnitIdentifier,
  councilName,
  onChangeCouncilName,
  districtName,
  onChangeDistrictName,
  charteredOrgName,
  onChangeCharteredOrgName,
  editable,
}: ConfirmIdentityStepProps) {
  return (
    <Stack spacing={2.5}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Let&apos;s confirm your unit
      </Typography>
      <Typography color="text.secondary">
        {editable
          ? 'We already know this much from your Scouting Ops account — just confirm the rest.'
          : 'This comes from your unit record in Scouting Ops. A unit admin can update it there.'}
      </Typography>

      <Alert severity="info">
        <strong>{unitName}</strong>
        {unitType ? ` · ${getUnitTypeFullLabel(unitType)}` : null}
      </Alert>

      <TextField
        label="Unit number"
        value={unitIdentifier}
        onChange={(event) => onChangeUnitIdentifier(event.target.value)}
        placeholder="e.g. 128"
        disabled={!editable}
        fullWidth
      />
      <TextField
        label="Chartered organization"
        value={charteredOrgName}
        onChange={(event) => onChangeCharteredOrgName(event.target.value)}
        placeholder="e.g. First Community Church"
        helperText={editable ? 'Optional — kept with the rest of your unit details in Scouting Ops.' : undefined}
        disabled={!editable}
        fullWidth
      />
      <TextField
        label="Council"
        value={councilName}
        onChange={(event) => onChangeCouncilName(event.target.value)}
        disabled={!editable}
        fullWidth
      />
      <TextField
        label="District"
        value={districtName}
        onChange={(event) => onChangeDistrictName(event.target.value)}
        disabled={!editable}
        fullWidth
      />
    </Stack>
  );
}
